<template>
    <UFormField label="Produkt" :ui="{label: '@2xl:hidden'}">
        <invoice-product-picker
            v-model:product="product"
            v-model:description="customDescription"
            theme="light"
            class="w-full"
            @product-created="handleCreatedProduct"
        />
    </UFormField>
    <UFormField v-if="product" label="Inntektskonto" :ui="{label: '@2xl:hidden'}">
        <togglable-edit>
            <template #static>{{ incomeAccountNo }}</template>
            <template #edit="{stopEdit}">
                <income-account-picker
                    v-model:account-no="incomeAccountNo"
                    :disabled="!product"
                    autofocus
                    default-open
                    theme="light"
                    class="w-full"
                    @update:account-no="() => stopEdit()"
                />
            </template>
        </togglable-edit>
    </UFormField>
    <div v-else />
    <UFormField :label="showProject ? 'Prosjekt':''" :ui="{label: '@2xl:hidden'}">
        <project-picker-new
            v-if="showProject"
            v-model="project"
            theme="light"
            class="w-full @2xl:max-w-36"
        />
    </UFormField>
    <UFormField :label="showDepartment ? 'Avdeling': ''" :ui="{label: '@2xl:hidden'}">
        <department-picker-new
            v-if="showDepartment"
            v-model="department"
            theme="light"
            class="w-full @2xl:max-w-36"
        />
    </UFormField>
    <UFormField label="Pris" :ui="{label: '@2xl:hidden'}">
        <product-line-price-input v-model="unitPrice" :disabled="!product" class="w-full"/>
    </UFormField>
    <UFormField label="Antall" :ui="{label: '@2xl:hidden'}">
        <product-line-quantity-input v-model="quantity" :disabled="!product" class="w-full"/>
    </UFormField>
    <UFormField :label="showDiscount ? 'Rabatt':''" :ui="{label: '@2xl:hidden'}">
        <discount-percent-input
            v-if="showDiscount"
            v-model="discount"
            :disabled="!product"
            class="w-full @2xl:w-18"
        />
    </UFormField>
    <UFormField :label="isVatLiable ? 'MVA':''" :ui="{label: '@2xl:hidden'}">
        <sales-vat-picker
            v-if="isVatLiable"
            v-model="vatOption"
            :disabled="!product"
            theme="light"
        />
    </UFormField>
    <UFormField label="">
        <div class="mt-2 @2xl:mt-0 pt-1 flex gap-1 @2xl:gap-0">
            <UButton
                variant="outline"
                title="Ny linje"
                color="neutral"
                icon="i-ph-plus"
                size="sm"
                class="w-1/3 @2xl:w-fit"
                label="Ny linje"
                :ui="{label: '@2xl:hidden'}"
                @click="addProductLine(index+1)"
            />
            <UButton
                variant="outline"
                title="Kopier linje"
                color="neutral"
                icon="i-ph-copy"
                size="sm"
                class="w-1/3 @2xl:w-fit"
                label="Kopier linje"
                :ui="{label: '@2xl:hidden'}"
                @click="copyLineAtIndex(index)"
            />
            <UButton
                variant="outline"
                color="error"
                icon="i-ph-x"
                size="sm"
                title="Fjern produktlinje"
                class="w-1/3 @2xl:w-fit"
                label="Fjern linje"
                :ui="{label: '@2xl:hidden'}"
                :disabled="invoiceData.productLines.length === 1"
                @click="deleteLine"
            />
        </div>
    </UFormField>
    <div class="text-right mt-4 pt-2 @2xl:mt-0 text-lg @2xl:text-base" data-cy="product-line-price">
        <span class="text-muted text-sm mr-2 @2xl:hidden">NOK</span>{{ formatCurrency(lineNetPrice) }}
    </div>
</template>

<script setup lang="ts">

import {ProductLine, productLineNetPrice} from '@/invoice/InvoiceCreator/invoiceProductLine'
import {computed, PropType} from 'vue'
import {useInvoiceCreator} from '@/invoice/invoice.service'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {useVatService} from '@/vat/vat.service'
import {formatCurrency} from '@/services/formatter.service'
import DiscountPercentInput from '@/invoice/InvoiceCreator/DiscountPercentInput.vue'
import {Product} from '@/products/product.types'
import {useProducts} from '@/products/product.service'
import {VatOption} from '@/vat/types/VatOptions'
import InvoiceProductPicker from '@/invoice/InvoiceCreator/InvoiceProductPicker.vue'
import IncomeAccountPicker from '@/bookkeeping-account/IncomeAccountPicker.vue'
import ProjectPickerNew from '@/dimensions/ProjectPickerNew.vue'
import DepartmentPickerNew from '@/dimensions/DepartmentPickerNew.vue'
import SalesVatPicker from '@/vat/SalesVatPicker.vue'
import ProductLinePriceInput from '@/invoice/InvoiceCreator/ProductLinePriceInput.vue'
import ProductLineQuantityInput from '@/invoice/InvoiceCreator/ProductLineQuantityInput.vue'

const {deleteLineAtIndex, updateProductLineAtIndex, addProductLine, copyLineAtIndex, invoiceData, isVatLiable, showDiscount} = useInvoiceCreator()
const {getProductByUuid} = useProducts()
const {getVatOptionById} = useVatService()
import {v4 as uuidv4} from 'uuid'

const props = defineProps({
    productLine: {
        type: Object as PropType<ProductLine>,
        required: true
    },
    index: {
        type: Number,
        required: true
    },
    showProject: {
        type: Boolean,
        default: false
    },
    showDepartment: {
        type: Boolean,
        default: false
    }
})

const product = computed<Product | undefined>({
    get: () => getProductByUuid(props.productLine.productUuid),
    set: (newProduct: Product | undefined) => {
        if (!newProduct) {
            updateProductLineAtIndex({
                    ...props.productLine,
                    tempUuid: uuidv4(),
                    productUuid: '',
                    productName: '',
                    customDescription: ''
                },
                props.index
            )
        } else {
            updateProductLineAtIndex(
                {
                    ...props.productLine,
                    tempUuid: uuidv4(),
                    productUuid: newProduct.uuid,
                    productName: newProduct.name,
                    bookkeepingAccount: newProduct.accountNo,
                    vatOption: findVatOptionFromIncomeAccount(newProduct.accountNo) || props.productLine.vatOption,
                    unitPrice: newProduct.retailPrice,
                    customDescription: newProduct.name
                },
                props.index
            )
        }
    }
})

const lineNetPrice = computed<number>(() => {
    return productLineNetPrice(props.productLine)
})

const customDescription = computed<string>({
    get: () => props.productLine.customDescription || '',
    set: (value: string) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                customDescription: value
            },
            props.index
        )
    }
})

const handleCreatedProduct = (created: Product) => {
    product.value = created
}

const deleteLine = () => {
    deleteLineAtIndex(props.index)
}

const findVatOptionFromIncomeAccount = (accountNo: string): VatOption | undefined => {
    const account = bookkeepingAccounts.value.find(a => a.accountNo === accountNo)
    if (account && account.vatSuggestionId) {
        return getVatOptionById(account.vatSuggestionId)
    }
}

const incomeAccountNo = computed({
    get: () => props.productLine?.bookkeepingAccount,
    set: (accountNo: string) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                bookkeepingAccount: accountNo,
                vatOption: findVatOptionFromIncomeAccount(accountNo) || props.productLine.vatOption
            },
            props.index
        )
    }
})

const unitPrice = computed({
    get: () => props.productLine.unitPrice,
    set: (value: number) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                unitPrice: value
            },
            props.index
        )
    }
})

const project = computed({
    get: () => props.productLine.project,
    set: (value: string) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                project: value
            },
            props.index
        )
    }
})

const department = computed({
    get: () => props.productLine.department,
    set: (value: string) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                department: value
            },
            props.index
        )
    }
})

const quantity = computed({
    get: () => props.productLine.quantity,
    set: (value: number) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                quantity: value
            },
            props.index
        )
    }
})

const discount = computed({
    get: () => props.productLine.discount,
    set: (value: number) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                discount: value
            },
            props.index
        )
    }
})

const vatOption = computed<VatOption>({
    get: () => props.productLine.vatOption,
    set: (value: VatOption) => {
        updateProductLineAtIndex(
            {
                ...props.productLine,
                vatOption: value
            },
            props.index
        )
    }
})

</script>